/**
 * RobotMascot — FIBI Holographic Faceted AI · v3.1 · VIVID EYES + MOUTH
 * ═══════════════════════════════════════════════════════════════════
 * Restauración del holograma facetado prisma, con ojos LIMA (#C7FF00)
 * y boca AMARILLA (#E0E621) super saturados y gruesos para que se noten
 * sin importar el color de las facetas subyacentes.
 */
import { motion } from "framer-motion";
import { useTheme } from "../lib/theme";

// Iridescent prism palettes — 3-4 color stops per gradient
const PALETTE_DARK = {
  idle:    { primary: "#00F0FF", secondary: "#E0E621", accent: "#FF2FB9", extra: "#7C3AED", warm: "#EA580C" },
  wave:    { primary: "#00F0FF", secondary: "#E0E621", accent: "#C7FF00", extra: "#38BDF8", warm: "#EA580C" },
  tour:    { primary: "#FF2FB9", secondary: "#00F0FF", accent: "#E0E621", extra: "#8B5CF6", warm: "#EC4899" },
  success: { primary: "#39FF7A", secondary: "#00F0FF", accent: "#E0E621", extra: "#10B981", warm: "#F59E0B" },
  alert:   { primary: "#FF2E4A", secondary: "#FF2FB9", accent: "#FFB800", extra: "#EF4444", warm: "#F97316" },
};

// Apple Vision Pro palette — neutral graphite + slate + a single deep Apple blue accent
// (no neon, no pastel pop; strictly Apple-esque grays with one focus color)
const PALETTE_LIGHT = {
  idle:    { primary: "#475569", secondary: "#94A3B8", accent: "#0071E3", extra: "#CBD5E1", warm: "#64748B" },
  wave:    { primary: "#334155", secondary: "#64748B", accent: "#0071E3", extra: "#94A3B8", warm: "#CBD5E1" },
  tour:    { primary: "#1F2937", secondary: "#475569", accent: "#0071E3", extra: "#64748B", warm: "#94A3B8" },
  success: { primary: "#0F766E", secondary: "#14B8A6", accent: "#0F172A", extra: "#5EEAD4", warm: "#94A3B8" },
  alert:   { primary: "#9F1239", secondary: "#E11D48", accent: "#0F172A", extra: "#FCA5A5", warm: "#94A3B8" },
};

// Vivid neon for DARK mode (lime eyes glow on cyberpunk face)
const EYES_COLOR_DARK  = "#C7FF00"; // Lima vivo
const MOUTH_COLOR_DARK = "#E0E621"; // Amarillo neón

// Apple aesthetic for LIGHT mode (deep Apple blue eyes, no neon)
const EYES_COLOR_LIGHT  = "#0071E3"; // Apple system blue
const MOUTH_COLOR_LIGHT = "#0F172A"; // Slate 900

export default function RobotMascot({ size = 120, variant = "idle", className = "", forceDark = false }) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = forceDark || themeIsDark;
  const PALETTE = isDark ? PALETTE_DARK : PALETTE_LIGHT;
  const c = PALETTE[variant] || PALETTE.idle;
  const bgFacet = isDark ? "#020B10" : "#FFFFFF";
  const EYES_COLOR  = isDark ? EYES_COLOR_DARK  : EYES_COLOR_LIGHT;
  const eyeBacking  = isDark ? "#020B10" : "#0F172A";
  const eyeStroke   = isDark ? "#FFFFFF" : "#FFFFFF";

  return (
    <div
      className={`relative inline-block select-none ${className}`}
      style={{ width: size, height: size * 1.15 }}
      data-testid="robot-mascot"
    >
      {/* Halo / aura — multicolor prism reflection */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: isDark
            ? `radial-gradient(circle, ${c.primary}55 0%, ${c.primary}00 55%)`
            : `conic-gradient(from 0deg at 50% 50%, ${c.primary}55, ${c.secondary}55, ${c.accent}55, ${c.warm}55, ${c.extra}55, ${c.primary}55)`,
          filter: `blur(${isDark ? 16 : 26}px)`,
        }}
        animate={isDark
          ? { scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }
          : { scale: [1, 1.15, 1], opacity: [0.55, 0.85, 0.55], rotate: [0, 360] }}
        transition={{ duration: isDark ? 3.2 : 12, repeat: Infinity, ease: "linear" }}
      />

      {/* Orbital rings */}
      <motion.svg
        viewBox="0 0 240 240"
        width={size}
        height={size * 1.15}
        className="absolute inset-0"
        animate={{ rotate: 360 }}
        transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
      >
        <ellipse cx="120" cy="145" rx="90" ry="24" fill="none" stroke={c.primary} strokeWidth={isDark ? "0.8" : "1.2"} strokeDasharray="2 6" opacity={isDark ? "0.5" : "0.65"} />
        <ellipse cx="120" cy="145" rx="70" ry="18" fill="none" stroke={c.accent} strokeWidth={isDark ? "0.6" : "1"} strokeDasharray="1 4" opacity={isDark ? "0.4" : "0.55"} />
      </motion.svg>

      {/* Main mascot */}
      <motion.svg
        viewBox="0 0 240 260"
        width={size}
        height={size * 1.15}
        className="relative"
        animate={{ y: [0, -6, 0], rotate: [-1, 1, -1] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <defs>
          <linearGradient id="fibi-face-a" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={c.primary} stopOpacity={isDark ? "0.9" : "1"} />
            {!isDark && <stop offset="50%" stopColor={c.warm} stopOpacity="0.85" />}
            <stop offset="100%" stopColor={c.secondary} stopOpacity={isDark ? "0.35" : "0.85"} />
          </linearGradient>
          <linearGradient id="fibi-face-b" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={c.accent} stopOpacity={isDark ? "0.75" : "1"} />
            {!isDark && <stop offset="55%" stopColor={c.secondary} stopOpacity="0.85" />}
            <stop offset="100%" stopColor={c.primary} stopOpacity={isDark ? "0.3" : "0.7"} />
          </linearGradient>
          <linearGradient id="fibi-face-c" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={c.secondary} stopOpacity={isDark ? "0.7" : "1"} />
            {!isDark && <stop offset="50%" stopColor={c.extra} stopOpacity="0.9" />}
            <stop offset="100%" stopColor={c.accent} stopOpacity={isDark ? "0.35" : "0.85"} />
          </linearGradient>
          <linearGradient id="fibi-face-d" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={c.primary} stopOpacity={isDark ? "0.85" : "1"} />
            {!isDark && <stop offset="60%" stopColor={c.accent} stopOpacity="0.65" />}
            <stop offset="100%" stopColor={bgFacet} stopOpacity={isDark ? "0.85" : "0.35"} />
          </linearGradient>
          <radialGradient id="fibi-core">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="35%" stopColor={c.primary} />
            <stop offset="100%" stopColor={c.primary} stopOpacity="0" />
          </radialGradient>

          {/* Extra strong glow filter for eyes/mouth */}
          <filter id="fibi-vivid-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="fibi-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation={isDark ? "2.2" : "1.4"} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Antenna beam */}
        <motion.line
          x1="120" y1="12" x2="120" y2="46"
          stroke={c.accent}
          strokeWidth={isDark ? "1.5" : "2"}
          strokeDasharray="2 3"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        />
        <motion.circle
          cx="120" cy="10" r={isDark ? "4" : "5"}
          fill={c.accent}
          filter="url(#fibi-glow)"
          animate={{ r: [3, 5, 3] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        />

        {/* Faceted head — icosahedral projection */}
        <g filter="url(#fibi-glow)">
          <polygon points="120,50 88,78 152,78" fill="url(#fibi-face-a)" stroke={c.primary} strokeWidth={isDark ? "1" : "1.5"} />
          <polygon points="88,78 68,120 100,138 120,110" fill="url(#fibi-face-b)" stroke={c.primary} strokeWidth={isDark ? "0.8" : "1.2"} opacity="0.92" />
          <polygon points="152,78 172,120 140,138 120,110" fill="url(#fibi-face-c)" stroke={c.primary} strokeWidth={isDark ? "0.8" : "1.2"} opacity="0.92" />
          <polygon points="88,78 120,110 152,78" fill="url(#fibi-face-d)" stroke={c.primary} strokeWidth={isDark ? "1.2" : "1.6"} />
          <polygon points="100,138 140,138 120,168" fill="url(#fibi-face-a)" stroke={c.primary} strokeWidth={isDark ? "0.8" : "1.2"} opacity={isDark ? "0.7" : "0.85"} />
          <polygon points="68,120 100,138 120,168 90,150" fill="url(#fibi-face-b)" stroke={c.primary} strokeWidth={isDark ? "0.6" : "1"} opacity={isDark ? "0.6" : "0.75"} />
          <polygon points="172,120 140,138 120,168 150,150" fill="url(#fibi-face-c)" stroke={c.primary} strokeWidth={isDark ? "0.6" : "1"} opacity={isDark ? "0.6" : "0.75"} />
        </g>

        {/* ═════ VIVID EYES — LIME circles with dark backing for max contrast ═════ */}
        {/* Eye backing plates (dark, rounded) */}
        <circle cx="105" cy="96" r="7" fill={eyeBacking} opacity={isDark ? "0.85" : "1"} />
        <circle cx="135" cy="96" r="7" fill={eyeBacking} opacity={isDark ? "0.85" : "1"} />
        <motion.circle
          cx="105" cy="96" r="5"
          fill={EYES_COLOR}
          stroke={eyeStroke} strokeWidth="0.5"
          filter={isDark ? "url(#fibi-vivid-glow)" : undefined}
          animate={{ opacity: [1, 0.5, 1], scale: [1, 1.12, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "105px 96px" }}
        />
        <motion.circle
          cx="135" cy="96" r="5"
          fill={EYES_COLOR}
          stroke={eyeStroke} strokeWidth="0.5"
          filter={isDark ? "url(#fibi-vivid-glow)" : undefined}
          animate={{ opacity: [1, 0.5, 1], scale: [1, 1.12, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.15 }}
          style={{ transformOrigin: "135px 96px" }}
        />
        {/* Tiny highlight dots on eyes */}
        <circle cx="103" cy="94" r="1.3" fill="#FFFFFF" opacity="0.9" />
        <circle cx="133" cy="94" r="1.3" fill="#FFFFFF" opacity="0.9" />

        {/* Faceted torso */}
        <g filter="url(#fibi-glow)">
          <polygon points="90,170 120,182 150,170 152,215 88,215" fill="url(#fibi-face-d)" stroke={c.primary} strokeWidth={isDark ? "1" : "1.4"} opacity={isDark ? "0.9" : "1"} />
          <polygon points="90,170 120,182 120,215 88,215" fill="url(#fibi-face-b)" stroke={c.primary} strokeWidth={isDark ? "0.6" : "1"} opacity={isDark ? "0.5" : "0.7"} />
          <polygon points="150,170 120,182 120,215 152,215" fill="url(#fibi-face-c)" stroke={c.primary} strokeWidth={isDark ? "0.6" : "1"} opacity={isDark ? "0.5" : "0.7"} />
        </g>

        {/* Core light chest */}
        <motion.circle
          cx="120" cy="196" r="9"
          fill="url(#fibi-core)"
          animate={{ scale: [1, 1.25, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Scanline sweep */}
        <motion.rect
          x="70" y="75" width="100" height="2"
          fill={c.primary}
          opacity={isDark ? "0.35" : "0.55"}
          filter="url(#fibi-glow)"
          animate={{ y: [75, 165, 75] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Data particles */}
        {[0, 1, 2, 3].map((i) => (
          <motion.circle
            key={i}
            cx={60 + i * 40}
            cy={230}
            r={isDark ? "1.5" : "2"}
            fill={c.accent}
            animate={{ cy: [230, 215, 230], opacity: [0.2, 1, 0.2] }}
            transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.3, ease: "easeInOut" }}
          />
        ))}

        {/* Pedestal */}
        <ellipse cx="120" cy="235" rx="46" ry="6" fill={c.primary} opacity={isDark ? "0.15" : "0.25"} />
        <ellipse cx="120" cy="235" rx="36" ry="4" fill={c.primary} opacity={isDark ? "0.35" : "0.5"} />
        <ellipse cx="120" cy="235" rx="22" ry="2.5" fill={c.primary} opacity={isDark ? "0.7" : "0.85"} />
      </motion.svg>
    </div>
  );
}
